import {
  useChatTransportMode,
  useChatTransportModeSettled,
  type ChatTransportMode,
} from '@/hooks/chat/useChatTransportMode';
import { usePrimumChatSession } from '@/hooks/chat/usePrimumChatSession';
import { useTwilio } from '@/hooks/useTwilio';

/** The Twilio Conversations session: token, client, recovery and its inbox events. */
function TwilioChatSession() {
  useTwilio();

  return null;
}

/** The session on Primum's own messages API, with no Twilio client at all. */
function PrimumChatSession() {
  usePrimumChatSession();

  return null;
}

/**
 * Which session a mode runs. `shadow` runs Twilio's: the user is served by
 * Twilio, and the Primum transport only runs beside it for comparison.
 */
function sessionFor(mode: ChatTransportMode) {
  switch (mode) {
    case 'twilio':
    case 'shadow':
      return TwilioChatSession;
    case 'primum':
      return PrimumChatSession;
  }
}

/**
 * Runs the chat session the `chat_transport` release toggle selects, and only
 * that one.
 *
 * A session is a component rather than a hook so that switching modes unmounts
 * one session and mounts the other: the outgoing session's teardown runs, and
 * two sessions never write the transport store at once. Mounted once, beside
 * the router, so it lives for the whole app regardless of which screen shows.
 * Two modes that share a session component keep the running session.
 *
 * Nothing runs until the mode has settled, so a cold start on flag defaults
 * does not build a session only to replace it.
 */
export function ChatSessionHost() {
  const mode = useChatTransportMode();

  const isSettled = useChatTransportModeSettled();

  if (!isSettled) {
    return null;
  }

  const Session = sessionFor(mode);

  return <Session />;
}
